'use client';
import { useRouter } from 'next/navigation';

import { cn } from '@/lib/utils';

import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import PostByLine, { PostByLineProps } from '@/decodo/features/blog/PostByLine';
import {
  PostTags,
  PostTopic,
} from '@/decodo/features/shared/BlogMetaData/PostTags';
import ImageStrapi, { ImageStrapiProps } from '../../atoms/Image/ImageStrapi';

export type ArticleItemProps = {
  slug: string;
  image?: ImageStrapiProps;
  releaseDate: string;
  timeSpentReading?: number;
  isNew?: boolean;
  herotitle?: string;
  description?: React.ReactNode;
  topics?: PostTopic[];
  author?: PostByLineProps['author'];
  size?: 'small' | 'large';
};

export default function ArticleItem({
  slug,
  image,
  releaseDate,
  timeSpentReading,
  isNew,
  herotitle,
  description,
  topics,
  author,
  size = 'large',
}: ArticleItemProps) {
  const sizesClasses = {
    small: {
      image: 'max-w-[100px] min-w-[100px] h-[100px]',
      description: 'hidden',
      root: 'flex',
      title: 'mb-3 line-clamp-2',
      blogInfoWrapper: 'pl-4',
      topics: 'pb-2',
    },
    large: {
      root: 'flex flex-1 flex-col',
      blogInfoWrapper: 'gap-3',
      image: 'w-full object-cover h-[200px]',
      description:
        'text-body-l line-clamp-2 text-gray-500 dark:text-gray-dark-400',
      topics: 'pt-[14px] md:pt-6 ',
    },
  }[size];

  const isBlogSlug = slug.startsWith('/blog');
  const resolvedSlug = isBlogSlug ? slug : `/blog/${slug}`;
  const router = useRouter();

  return (
    <div className={sizesClasses.root}>
      {!!image && (
        <LinkButton
          data-tracker-component='article-image'
          href={resolvedSlug}
          classNames={{
            inner: 'overflow-hidden object-cover rounded mb-6',
          }}
          className={sizesClasses.image}
          textWrap
        >
          <ImageStrapi
            block={image ?? {}}
            inner={{
              classNames: {
                container: 'overflow-hidden rounded-xl',
                image: '!object-cover min-h-[200px]',
              },
              sizes: '200px',
            }}
            width={size === 'small' ? 200 : undefined}
            height={size === 'small' ? 200 : undefined}
          />
        </LinkButton>
      )}
      <div
        className={cn('flex flex-col text-left', sizesClasses.blogInfoWrapper)}
      >
        <div className={sizesClasses.topics}>
          {((isNew && Number(topics?.length)) || Number(topics?.length)) >
            0 && <PostTags isNew={isNew ?? false} topics={topics ?? []} />}
        </div>
        <LinkButton
          variant='text'
          color='secondary'
          slim
          href={resolvedSlug}
          textWrap
          underline={false}
          data-tracker-component='article-title'
        >
          <Text
            color='gray-950'
            className={cn('text-subtitle-l-d font-serif', sizesClasses.title)}
          >
            {herotitle}
          </Text>
        </LinkButton>
        {!!description && (
          <div
            className={cn(sizesClasses.description, 'cursor-pointer')}
            onClick={() => router.push(resolvedSlug)}
            data-tracker-component='article-description'
          >
            <div className={sizesClasses.description}>{description}</div>
          </div>
        )}
        <div className='flex-wrap items-center gap-4 '>
          <PostByLine
            releaseDate={releaseDate}
            timeSpentReading={timeSpentReading}
            author={author}
            disableImage={size === 'small'}
          />
        </div>
      </div>
    </div>
  );
}
